import numeral from 'numeral'
import { get } from 'lodash'
import { RESERVE_LABELS } from '@adc/blueprint'
import {
  formatAuctionDate,
  formatAuctionDateRange,
  formatAuctionTime,
} from '../formatters'
import { getTopKPrioritizedBadges } from '../badges'
import { absoluteToRelativeUrl } from '../url'
import defaultImage from './assets/no_image_available.png'

const isTrustee = asset =>
  (asset.product_type + '').toLowerCase() === 'foreclosure/trustee'

const openingBid = asset => {
  // Search returns a slash for all invalid starting bids, so we'll do that here too
  if (asset.suppress_opening_bid) return '-'
  return asset.starting_bid ? numeral(asset.starting_bid).format('$0,0') : '-'
}

const valuationEstimate = asset => {
  if (!asset.valuation_available) return 'Not Available'
  return asset.valuation_estimate
    ? numeral(asset.valuation_estimate).format('$0,0')
    : 'Not Available'
}

const valuationEstimateCard2 = asset => {
  if (!asset.valuation_estimate) return ''
  return asset.valuation_estimate
    ? numeral(asset.valuation_estimate).format('$0,0')
    : ''
}

const priceEstimate = asset => {
  if (!asset.show_reserve) return ''
  return asset.price_estimate
    ? numeral(asset.price_estimate).format('$0,0')
    : ''
}

const auctionDate = asset => {
  const trustee = isTrustee(asset)
  const auctionStartDate = asset.auction_start_date
  const auctionEndDate = asset.auction_end_date
  const auctionDateBasic = formatAuctionDate(auctionStartDate)
  const auctionDateInfo = formatAuctionDateRange(
    auctionStartDate,
    auctionEndDate
  )
  const isOnlineBiddingAsset = trustee
    ? asset.is_asset_online_event && asset.is_auctioneer_assigned
    : asset.is_asset_online_event
  return isOnlineBiddingAsset ? auctionDateInfo : auctionDateBasic
}

const auctionTime = asset => {
  const trustee = isTrustee(asset)
  const saleTime = trustee ? asset.sale_time : asset.venue_time
  const auctionTime = formatAuctionTime(saleTime)
  return auctionTime
}

const assetPrices = asset => {
  const estResaleValue = valuationEstimateCard2(asset)
  const reserve = priceEstimate(asset)

  return [
    {
      label: RESERVE_LABELS.EST_CREDIT_BID,
      amount: `${reserve}`,
    },
    {
      label: 'Est. Market Value',
      amount: `${estResaleValue}`,
    },
  ]
}

const assetRentalRange = asset => {
  const high = asset.estimated_rent_high
    ? numeral(asset.estimated_rent_high).format('$0,0')
    : ''
  const mid = asset.estimated_rent_mid
    ? numeral(asset.estimated_rent_mid).format('$0,0')
    : ''
  const rentalRange = {
    high,
    mid,
  }
  return rentalRange
}

const isPropertyHot = asset =>
  asset && asset.property_is_hot ? asset.property_is_hot : false

// TODO: Replace this with https://git.ten-x.io/projects/RA/repos/resi-search/browse/src/common/v2/models/asset.js
export const convertPropertyToCard2 = asset => {
  const availability = auctionDate(asset)
  const startingBid = openingBid(asset)
  const startTime = auctionTime(asset)
  const estResaleValue = valuationEstimateCard2(asset)
  const reserve = priceEstimate(asset)
  const isHot = isPropertyHot(asset)
  // NOTE: We need this to be a relative url since we don't want the backend to care about the sub-domain of specific
  // environments. We don't want that coupling.
  const relativePdpUrl = absoluteToRelativeUrl(asset.pdp_url)

  return {
    propertyId: `${get(asset, 'global_property_id')}`,
    auctionType: get(asset, 'is_asset_online_event') ? 'Online' : 'Live',
    assetType: isTrustee(asset)
      ? 'Foreclosure Sale'
      : get(asset, 'product_type'),
    addressLine1: get(asset, 'property_address'),
    addressLine2: `${get(asset, 'property_city', '')}, ${get(
      asset,
      'property_state',
      ''
    )} ${get(asset, 'property_zip', '')}`,
    isHot,
    availability,
    startingBid,
    estResaleValue,
    reserve,
    isTrustee: isTrustee(asset),
    startTime,
    imgSource: get(asset, 'thumbnail', defaultImage),
    beds: `${get(asset, 'beds', '')}`,
    baths: `${get(asset, 'baths', '')}`,
    sqft: asset.sqft ? numeral(asset.sqft).format('0,0') : undefined,
    pdpUrl: relativePdpUrl,
    badges: getTopKPrioritizedBadges(asset),
    rentalRange: assetRentalRange(asset),
    prices: assetPrices(asset),
  }
}

export const convertPropertyToCard = asset => {
  const availability = auctionDate(asset)
  const startingBid = openingBid(asset)
  const startTime = auctionTime(asset)
  const estResaleValue = valuationEstimate(asset)
  const reserve = priceEstimate(asset)
  // NOTE: We need this to be a relative url since we don't want the backend to care about the sub-domain of specific
  // environments. We don't want that coupling.
  const relativePdpUrl = absoluteToRelativeUrl(asset.pdp_url)

  return {
    propertyId: `${get(asset, 'global_property_id')}`,
    auctionType: get(asset, 'is_asset_online_event') ? 'Online' : 'Live',
    assetType: isTrustee(asset)
      ? 'Foreclosure Sale'
      : get(asset, 'product_type'),
    addressLine1: get(asset, 'property_address'),
    addressLine2: `${get(asset, 'property_city', '')}, ${get(
      asset,
      'property_state',
      ''
    )} ${get(asset, 'property_zip', '')}`,
    availability,
    startingBid,
    estResaleValue,
    reserve,
    isTrustee: isTrustee(asset),
    startTime,
    imgSource: get(asset, 'thumbnail', defaultImage),
    beds: `${get(asset, 'beds', '')}`,
    baths: `${get(asset, 'baths', '')}`,
    sqft: asset.sqft ? numeral(asset.sqft).format('0,0') : undefined,
    pdpUrl: relativePdpUrl,
  }
}
